import { Box, Container, Heading, Text, Flex, Stack, Circle } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/color-mode';
import { HelpCircle, CheckCircle, Eye } from 'lucide-react';

interface StepProps {
  title: string;
  description: string;
  icon: React.ReactElement;
  stepNumber: number;
}

const Step = ({ title, description, icon, stepNumber }: StepProps) => {
  return (
    <Stack direction={{ base: 'column', md: 'row' }} gap={8} align="center">
      <Circle 
        size="80px" 
        bg="brand.primary" 
        color="gray.800"
        position="relative"
        _after={{
          content: '""',
          position: 'absolute',
          bottom: '-50px',
          left: '50%',
          transform: 'translateX(-50%)',
          height: stepNumber < 4 ? '50px' : '0px',
          width: '2px',
          bg: 'brand.secondary',
          display: { base: 'none', md: 'block' }
        }}
      >
        <Flex direction="column" align="center" justify="center">
          {icon}
          <Text fontSize="xs" mt={1} fontWeight="bold">Step {stepNumber}</Text>
        </Flex>
      </Circle>
      <Box flex={1}>
        <Heading as="h3" textStyle="subheading" mb={2}>{title}</Heading>
        <Text color="gray.600">{description}</Text>
      </Box>
    </Stack>
  );
};

const HowItWorksSection = () => {
  return (
    <Box id="how-it-works" py={20} bg="gray.50">
      <Container maxW="1200px">
        <Box textAlign="center" mb={16}>
          <Heading
            as="h2"
            textStyle="heading"
            position="relative"
            mb={5}
            _after={{
              content: '""',
              width: '70px',
              height: '4px',
              backgroundColor: 'brand.secondary',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            How DIYBuddy Works
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="800px" mx="auto" mt={8}>
            Our simple process helps you solve home issues or complete DIY projects with ease.
          </Text>
        </Box>

        <Stack gap={{ base: 12, md: 16 }} direction="column" maxW="800px" mx="auto">
          <Step
            icon={<HelpCircle size={32} />}
            title="Describe Your Issue or Project"
            description="Tell or show DIYBuddy what you're dealing with - whether it's a leaky faucet, strange noise from an appliance, or a replacement part you need to identify."
            stepNumber={1}
          />
          
          <Step
            icon={<CheckCircle size={32} />}
            title="Get Diagnosis, Part Identification, or Project Plan"
            description="Our AI will help you diagnose home problems, walking you through diagnosing step by step, or create a detailed plan for your DIY project."
            stepNumber={2}
          />
          
          <Step
            icon={<Eye size={32} />}
            title="DIY or Find a Professional"
            description="Follow our step-by-step instructions to tackle the issue yourself, order the right replacement parts, or get directed to the right type of professional."
            stepNumber={3}
          />
        </Stack>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
